import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import api from "../services/api"
import { API_ENDPOINTS } from "../config/api"

const SensorDetailsModal = ({ open, handleClose, sensorId, sensorTypeId }) => {
  const [sensorDetails, setSensorDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchSensorDetails = async () => {
      if (!sensorId && !sensorTypeId) return
      try {
        setLoading(true)
        const sensorResponse = await api.get(API_ENDPOINTS.SENSOR.GET_ONE(sensorId))
        const sensorData = sensorResponse.data.sensor
        const sensorTypeResponse = await api.get(API_ENDPOINTS.SENSOR_TYPE.GET_ONE(sensorTypeId))
        const sensorTypeData = sensorTypeResponse.data.sensorType
        if (sensorTypeData.name === "AIS") {
          const clientIdResponse = await api.get(API_ENDPOINTS.CLIENT.GET_ME)
          if (clientIdResponse.status === 200 &&  clientIdResponse.data.client.client_number) {
            setSensorDetails({ ...sensorData, sensorTypeName: sensorTypeData.name, clientNumber: clientIdResponse.data.client.client_number })
          } else {
            setSensorDetails({ ...sensorData, sensorTypeName: sensorTypeData.name })
          }
        } else {
          setSensorDetails({ ...sensorData, sensorTypeName: sensorTypeData.name })
        }
        setLoading(false)
      } catch (err) {
        console.error("Error fetching sensor details:", err)
        setError("Failed to fetch sensor details. Please try again.")
        setLoading(false)
      }
    }

    if (open) {
      fetchSensorDetails()
    }
  }, [open, sensorId, sensorTypeId])

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    )
  }

  if (error) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>
          <Typography color="error">{error}</Typography>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Sensor Details
        <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {sensorDetails && (
          <TableContainer component={Paper} sx={{ marginTop: '2px', boxShadow: "3px 3px 8px rgba(0,0,0,0.2)", borderRadius: '8px' }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Name
                  </TableCell>
                  <TableCell>{sensorDetails.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Sensor Type
                  </TableCell>
                  <TableCell>{sensorDetails.sensorTypeName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    IMEI
                  </TableCell>
                  <TableCell>{sensorDetails.imei}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Serial Number
                  </TableCell>
                  <TableCell>{sensorDetails.serial_number}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Latitude
                  </TableCell>
                  <TableCell>{sensorDetails.latitude || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Longitude
                  </TableCell>
                  <TableCell>{sensorDetails.longitude || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Status
                  </TableCell>
                  <TableCell>{sensorDetails.is_active ? "Active" : "Inactive"}</TableCell>
                </TableRow>
                {sensorDetails.sensorTypeName === "CCTV" && (
                  <>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                        HLS
                      </TableCell>
                      <TableCell>{sensorDetails.url}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                        RTSP
                      </TableCell>
                      <TableCell>{sensorDetails.rtsp}</TableCell>
                    </TableRow>
                  </>
                )}
                {sensorDetails.sensorTypeName === "AIS" && (
                  <>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                        IP
                      </TableCell>
                      <TableCell>{sensorDetails.ais_ip}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                        PORT
                      </TableCell>
                      <TableCell>{sensorDetails.ais_port}</TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Created At
                  </TableCell>
                  <TableCell>{new Date(sensorDetails.created_at).toLocaleString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: "bold" }}>
                    Updated At
                  </TableCell>
                  <TableCell>{new Date(sensorDetails.updated_at).toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SensorDetailsModal
